// Custom hook for wallet connection functionality
import { useState, useEffect, useCallback } from "react";
import { useNFTMarketplace } from "../contexts/NFTMarketplaceContext";
import { getMetaMaskProvider } from "../utils/metamaskProvider";

export const useWalletConnection = () => {
  const {
    isWalletConnected,
    walletAddress,
    networkId,
    isLoading,
    error,
    checkWalletConnection,
    connectWallet,
    disconnectWallet,
    fetchWalletBinding,
    verifyWalletBinding,
    getWalletConnectionStatus,
    detectWalletProviders,
    WALLET_PROVIDERS,
  } = useNFTMarketplace();

  const [connectionAttempted, setConnectionAttempted] = useState(false);

  // Check if user has connected their wallet - main function
  const hasWalletConnected = useCallback(async () => {
    try {
      const status = await checkWalletConnection();
      return status.isConnected;
    } catch (error) {
      console.error("Error checking wallet connection:", error);
      return false;
    }
  }, [checkWalletConnection]);

  // Enhanced connection function with provider selection
  const connectToWallet = useCallback(
    async (providerType) => {
      setConnectionAttempted(true);

      try {
        const result = await connectWallet(providerType);

        if (result.success) {
          return {
            success: true,
            address: result.address,
            chainId: result.chainId,
            message: "Wallet connected successfully",
          };
        } else {
          return {
            success: false,
            error: result.error,
            message: "Failed to connect wallet",
          };
        }
      } catch (error) {
        return {
          success: false,
          error: error.message,
          message: "Unexpected error during wallet connection",
        };
      }
    },
    [connectWallet],
  );

  // Get detailed wallet information
  const getWalletInfo = useCallback(() => {
    const status = getWalletConnectionStatus();
    const providers = detectWalletProviders();

    return {
      ...status,
      hasMetaMask: providers.some((p) => p.type === WALLET_PROVIDERS.METAMASK),
      hasCoinbase: providers.some(
        (p) => p.type === WALLET_PROVIDERS.COINBASE_SMART_WALLET,
      ),
      availableProviders: providers,
      connectionAttempted,
    };
  }, [
    getWalletConnectionStatus,
    detectWalletProviders,
    WALLET_PROVIDERS,
    connectionAttempted,
  ]);

  // Auto-check connection on mount
  useEffect(() => {
    hasWalletConnected();
  }, [hasWalletConnected]);

  return {
    // State
    isConnected: isWalletConnected,
    address: walletAddress,
    networkId,
    isLoading,
    error,
    connectionAttempted,

    // Functions
    hasWalletConnected,
    connectToWallet,
    disconnect: disconnectWallet,
    fetchWalletBinding,
    verifyWalletBinding,
    getWalletInfo,

    // Utilities
    isMetaMaskAvailable: () => Boolean(getMetaMaskProvider()),
    isCoinbaseAvailable: () => true, // Always available through SDK

    // Constants
    WALLET_PROVIDERS,
  };
};
